import { useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  checkIsAllDomainsSelected,
  getNodeDescendantIds,
  getNodeWithDescendantsOptions,
  getSelectedDomains,
} from '@components/EntityDrawers/components/inputs/DomainDropdown/helpers';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { ISelectOption } from '@constants/entities/ui';
import useProjectId from '@hooks/useProjectId';
import { Checkbox, Stack, Typography } from '@mui/material';
import { treeItemClasses, TreeView } from '@mui/x-tree-view';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { renderTree } from '@views/Project/components/ScopeFilterModal/ScopeFilterModal';
import { SelectHandler } from '@views/Project/components/ScopeFilterModal/TreeItem';
import {
  buildTree,
  getScopeRowIndent,
  getTreeCheckState,
} from '@views/Project/helpers';

const DomainsPaperBody = () => {
  const projectId = useProjectId();
  const { data: projectDomains } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );
  const [expanded, setExpanded] = useState<string[]>([]);

  /** Cache of individual domain selections before enterprise was toggled on */
  const cachedDomainsRef = useRef<ISelectOption[]>([]);

  const [wideDomain] = useMemo(() => {
    return buildTree(projectDomains ?? []);
  }, [projectDomains]);

  const nonEnterpriseTopLevelCount = wideDomain.children?.length ?? 0;
  const shouldAutoCollapseToWide = nonEnterpriseTopLevelCount > 1;

  /**
   * Build the full list of all individual domain options from the tree
   * (used when expanding from enterprise back to individual selections).
   */
  const buildAllDomainOptions = (): ISelectOption[] =>
    wideDomain.children?.flatMap((child) =>
      getNodeWithDescendantsOptions(wideDomain, child.id),
    ) ?? [];

  const hanleClick = (nodeId: string) => {
    setExpanded((ids) =>
      ids.includes(nodeId)
        ? ids.filter((id) => id !== nodeId)
        : [...ids, nodeId],
    );
  };

  const handleSelect =
    (onChange: (value: any) => void, currentValue: any): SelectHandler =>
    ({ nodeId, checked }) => {
      const isCurrentlyWideSelected = currentValue?.some(
        (d: ISelectOption) => d.value === wideDomain.id,
      );

      const idsToExpand = getNodeDescendantIds(wideDomain, nodeId);

      const removeNode = (domains: ISelectOption[]) =>
        domains.filter(
          (d) => String(d.value) !== nodeId && d.value !== wideDomain.id,
        );

      // Unchecking while enterprise is active:
      // expand enterprise to individuals and remove only the clicked node (keep descendants selected)
      if (!checked && isCurrentlyWideSelected) {
        const expandedDomains =
          cachedDomainsRef.current.length > 0
            ? [...cachedDomainsRef.current]
            : buildAllDomainOptions();

        setExpanded((prev) => Array.from(new Set([...prev, ...idsToExpand])));
        onChange(removeNode(expandedDomains));
        cachedDomainsRef.current = [];
        return;
      }

      if (checked) {
        const toAdd = getNodeWithDescendantsOptions(wideDomain, nodeId);

        setExpanded((prev) => Array.from(new Set([...prev, ...idsToExpand])));

        const mergedMap = new Map<string, ISelectOption>();
        (currentValue ?? []).forEach((item: ISelectOption) =>
          mergedMap.set(String(item.value), item),
        );
        toAdd.forEach((item) => mergedMap.set(String(item.value), item));

        const newDomainsArray = Array.from(mergedMap.values());

        const { isAllDomainsSelected, isWideSelected } =
          checkIsAllDomainsSelected({
            projectDomains: projectDomains ?? [],
            selectedDomains: newDomainsArray,
          });

        if (
          isAllDomainsSelected &&
          !isWideSelected &&
          shouldAutoCollapseToWide
        ) {
          cachedDomainsRef.current = [...newDomainsArray];
          onChange([{ value: wideDomain.id, label: wideDomain.name }]);
          return;
        }

        onChange(newDomainsArray);
      } else {
        setExpanded((prev) => Array.from(new Set([...prev, ...idsToExpand])));
        onChange(removeNode(currentValue ?? []));
      }
    };

  return (
    <Stack>
      <Controller
        render={({ field: { value, onChange } }) => {
          const isWideSelected = value?.some(
            (domain: ISelectOption) => domain.value === wideDomain.id,
          );

          const selectedDomains = getSelectedDomains({
            selectedDomains: value,
            wideDomain,
          });

          const selectedSet = new Set(selectedDomains);
          if (isWideSelected) {
            selectedSet.add(wideDomain.id);
          }

          const wideCheckState = getTreeCheckState(wideDomain, selectedSet, {
            isDerivedFromSubtree: true,
          });

          return (
            <>
              <Stack direction="row" px={3} py={2} alignItems="center" gap={2}>
                <Checkbox
                  checked={wideCheckState === 'checked'}
                  indeterminate={wideCheckState === 'indeterminate'}
                  onChange={(_, checked) => {
                    if (checked) {
                      // Cache current selections, set form to enterprise only
                      cachedDomainsRef.current = (value ?? []).filter(
                        (d: ISelectOption) => d.value !== wideDomain.id,
                      );
                      onChange([
                        {
                          value: wideDomain.id,
                          label: wideDomain.name,
                        },
                      ]);
                    } else {
                      // Restore cached individual selections
                      onChange(cachedDomainsRef.current);
                      cachedDomainsRef.current = [];
                    }
                  }}
                />

                <Typography color="grey.900">{wideDomain.name}</Typography>
              </Stack>

              <TreeView
                expanded={expanded}
                disableSelection
                multiSelect
                selected={selectedDomains}
                sx={{
                  pl: `${getScopeRowIndent(1)}px`,
                  [`& .${treeItemClasses.group}`]: {
                    ml: `${getScopeRowIndent(2) - getScopeRowIndent(1)}px`,
                  },
                }}
              >
                {wideDomain.children?.map((item) =>
                  renderTree({
                    node: item,
                    selected: selectedSet,
                    onSelect: handleSelect(onChange, value),
                    onClick: hanleClick,
                    sx: { p: '8px 12px' },
                  }),
                )}
              </TreeView>
            </>
          );
        }}
        name={CommonKeys.Domains}
      />
    </Stack>
  );
};

export default DomainsPaperBody;
